/**
 * SubjectBreakdown — everyone in the frame, not just the one the record was opened from.
 *
 * ── THE QUESTION THIS ANSWERS ──
 *
 * A search result is ONE PERSON. The frame it came from held seven, and the moment an operator
 * opens that record their next question is about the frame: how many were there, and who else.
 *
 * Nothing on screen could answer it. The card describes one person. The result LIST cannot be
 * read as the frame either, and that is the subtle part — the list is filtered, so a search for
 * "red top" contains exactly one of those seven people and silently omits the rest. Only the
 * projection knows the whole frame, and this is it: every subject the server indexed for this
 * event, in the module's own order, with the attributes the search actually matched on.
 *
 * ── WHY IT IS A ROW OF FACES AND NOT A LIST OF SENTENCES ──
 *
 * Seven people times five attributes is thirty-five label/value pairs, and written out as text
 * that is a wall nobody reads — the reader is looking for a PERSON, and the fastest way to find
 * a person is to look at them. So each subject leads with its own crop, the one the module cut
 * for exactly this purpose, and the attributes sit beside it as a small labelled grid rather
 * than a run-on line.
 *
 * Colours carry a swatch as well as their name, for the same reason the filter bar's colour
 * dimensions are a swatch grid rather than a checkbox list: colour is recognised, not read.
 * The name stays — a swatch alone is unreadable to anyone who cannot distinguish the two it sits
 * between, and "Grey" is also the word the operator will type into the filter.
 *
 * ── WHY THE ROWS ARE CLICKABLE ──
 *
 * The record has two halves — the snapshot strip above and this list below — and they are about
 * the same seven people. Without a link between them, finding "the one in the orange top" in the
 * strip means counting crops. Clicking a subject here selects their frame up there, so reading
 * the list and looking at the picture are one motion.
 *
 * ── ORDER IS THE MODULE'S, ALWAYS ──
 *
 * `#2` here, `person Crop 2` in the strip, and `personCrop2` in the payload are the same person.
 * Re-sorting to put the opened subject first would break that correspondence — the reader would
 * look at "the third one down" in two places and get two different people — so the opened one is
 * MARKED, never moved.
 *
 * ── WHICH FIELDS ARE COLUMNS AT ALL IS DECIDED PER FRAME ──
 *
 * The first cut showed every field the subject's kind defines, on every card. On live data the
 * shipping module emits two of the five — so seven people produced twenty-one empty cells, and
 * the panel read as a record that had failed to load rather than as one where the detector
 * reports two things.
 *
 * A field is a column here when SOMEBODY in this frame has a value for it. Then `—` on another
 * card is a real finding: it says that person specifically was not read, next to someone who
 * was. A field NOBODY has is not a column — it is named once, under the list, as something this
 * camera does not collect. One line instead of twenty-one dashes, and it says more.
 *
 * `unknown` is always printed and never folded into `—`. It is the model saying it looked and
 * could not tell, which is a different fact from not being read at all, and a frame where six of
 * seven lower-body colours were occluded must not look like a frame nobody was asked about.
 */
import { useMemo, useState, type ReactNode } from 'react';
import type { EventFact } from './EventDetail';
import { Swatch } from '../search/FilterControls';
import {
  attrWord,
  COLOR_FIELDS,
  FIELD_LABEL,
  SUBJECT_FALLBACK,
  titleCase,
  type SubjectKey,
} from '../search/searchModel';

/** One projected subject, as `/model-events/:model/:id` returns them on `data.subjects`. */
export interface EventSubject {
  subject_seq: number;
  subject_kind: string | null;
  subject_id?: string | null;
  subject_label?: string | null;
  is_known?: boolean;
  track_id?: number | null;
  attributes?: Record<string, string | string[]> | null;
  attr_conf?: Record<string, number> | null;
  /** The key inside the event's `image_urls` holding this subject's own crop. */
  crop_key?: string | null;
}

export interface SubjectBreakdownProps {
  subjects?: EventSubject[] | null;
  /**
   * The subject this record was opened from, by its `subject_seq`. Marked, never re-ordered.
   * Absent where the record was not reached from a per-subject result (a report table row), and
   * the list then simply has nothing highlighted.
   */
  highlightSeq?: number | null;
  /**
   * This subject's crop, as a picture — supplied by the HOST, because the two apps cannot load
   * one the same way. On a node the projection's URLs are directly fetchable; from the cloud
   * they point inside the node's network and each frame has to be streamed through the tunnel.
   *
   * Omitted, every subject shows a numbered placeholder. That is a worse list, not a broken one,
   * which is the right outcome for a model that ships no per-subject crops at all.
   */
  renderThumb?: (subject: EventSubject) => ReactNode;
  /**
   * Show this subject's frame in the snapshot viewer above. Given, the rows become buttons.
   *
   * Takes the SUBJECT rather than the crop key so a host can decide what to do with one that
   * has no crop — today that means "leave the viewer alone", which is better than selecting
   * somebody else's picture.
   */
  onSelect?: (subject: EventSubject) => void;
  className?: string;
}

/** One attribute's values, as words. An array is one person carrying several things. */
function valuesOf(raw: unknown): string[] {
  return (Array.isArray(raw) ? raw : raw == null ? [] : [raw])
    .filter((v): v is string => typeof v === 'string' && v.length > 0);
}

/**
 * Which fields to print for a subject, and in which order.
 *
 * Starts from the SAME subject definition the filter bar is built from, so a record shows exactly
 * the dimensions a reader can search by — a field visible here and missing from the filter bar
 * reads as a broken filter bar, and the reverse reads as a detector that stopped reporting.
 *
 * Then ANY other key the subject actually carries is appended. That list is a build-time fallback
 * (the live one comes from `/search/schema`, which this record does not fetch), so a field the
 * server has learned to collect since this build would otherwise be silently invisible in the one
 * place showing everything the server knows about this detection.
 *
 * `plate` is pulled to the front for vehicles: it is an identity rather than a vocabulary, so it
 * is not in `fields`, and it is the first thing anyone wants off a vehicle.
 */
function fieldsFor(subject: EventSubject): string[] {
  const kind = subject.subject_kind;
  const def = SUBJECT_FALLBACK[(kind || '') as SubjectKey];
  const known = def ? def.fields : [];
  const ordered = kind === 'vehicle' ? ['plate', ...known] : [...known];
  for (const key of Object.keys(subject.attributes || {})) {
    if (!ordered.includes(key)) ordered.push(key);
  }
  return ordered;
}

/**
 * "7 people" / "4 vehicles" / "3 subjects" — the headline the reader came for.
 *
 * Counted from the rows themselves rather than from the module's own `people` field. They can
 * legitimately differ: a detection below the confidence floor is reported by the module and NOT
 * indexed, so `people: 7` with six rows is correct and means one was too uncertain to record.
 * Printing the module's number over a list of six would look like the list had lost one.
 */
function headline(subjects: EventSubject[]): string {
  const kinds = new Set(subjects.map((s) => s.subject_kind).filter(Boolean));
  const n = subjects.length;
  if (kinds.size === 1) {
    const kind = [...kinds][0];
    if (kind === 'person') return `${n} ${n === 1 ? 'person' : 'people'}`;
    if (kind === 'vehicle') return `${n} ${n === 1 ? 'vehicle' : 'vehicles'}`;
  }
  return `${n} ${n === 1 ? 'subject' : 'subjects'}`;
}

/**
 * The fields worth showing for THIS frame, split into the two lines a card has.
 *
 * `lead`   the colour-bearing fields — what tells two people apart at a glance, and what an
 *          operator actually searches by. Rendered large, with a swatch.
 * `rest`   everything else somebody in the frame carries.
 * `absent` fields the subject's kind defines that NOBODY here has. Named once under the list.
 *
 * Decided across the whole frame rather than per card, so the cards line up: a column that
 * exists for one person exists for all of them, and `—` means "this one was not read" rather
 * than "this field does not exist".
 */
/**
 * What goes on the big line, per subject kind.
 *
 * NOT "the colour fields", which is what this was and which read badly on a vehicle: it put
 * "Grey colour" in the headline and demoted "Bus" to the muted line, when the TYPE is what
 * identifies a vehicle. For a person the two garment colours genuinely are the identifying pair —
 * it is what an operator searches by and what tells two people in one frame apart.
 *
 * A kind with no entry falls back to whatever colours it has, which is a reasonable guess for a
 * subject this build has never heard of and better than an empty headline.
 */
const LEAD_FIELDS: Record<string, string[]> = {
  person: ['upper_color', 'lower_color'],
  vehicle: ['vehicle_type', 'vehicle_color'],
};

function planFor(subjects: EventSubject[]): { lead: string[]; rest: string[]; absent: string[] } {
  const defined: string[] = [];
  for (const s of subjects) {
    for (const f of fieldsFor(s)) if (!defined.includes(f)) defined.push(f);
  }
  const has = (f: string) => subjects.some((s) => valuesOf(s.attributes?.[f]).length > 0);

  const kinds = new Set(subjects.map((s) => s.subject_kind).filter(Boolean) as string[]);
  // One kind per frame in practice (a person model does not emit vehicles). Mixed, nothing leads
  // rather than one kind's headline being imposed on the other's rows.
  const wanted = kinds.size === 1 ? LEAD_FIELDS[[...kinds][0]] : null;
  const leadSet = wanted || defined.filter((f) => COLOR_FIELDS.has(f));

  const present = defined.filter(has);
  return {
    lead: present.filter((f) => leadSet.includes(f)),
    rest: present.filter((f) => !leadSet.includes(f)),
    absent: defined.filter((f) => !has(f)),
  };
}

/**
 * One headline field: the value large, with a swatch where it is a colour.
 *
 * The field's own word trails the value only for a COLOUR — "Black upper" needs it to say which
 * garment, where "Bus" does not need "type" after it and reads worse for having it. Both are
 * still labelled in the sense that matters: the reader can tell what they are looking at.
 */
function LeadValue({ field, values }: { field: string; values: string[] }) {
  const isColor = COLOR_FIELDS.has(field);
  const label = (FIELD_LABEL[field] || titleCase(field)).toLowerCase();

  if (!values.length) {
    // The field IS a column for this frame (somebody has one), so an empty one is a finding
    // about this subject — said in words, because a blank gap says nothing.
    return (
      /* Solid tokens: `text-muted-foreground/70` and `border-muted-foreground/40` are bare
         `var()` colours with an opacity modifier, which Tailwind v3 drops silently — so this
         line inherited whatever colour surrounded it and the dot fell back to the preflight
         border, which is the one thing it must not look like: a value that was read. */
      <span className="inline-flex items-center gap-1 text-[12px] text-muted-foreground opacity-70">
        <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full border border-dashed border-muted-foreground" />
        <span>no {label}</span>
      </span>
    );
  }
  return (
    <span className="inline-flex min-w-0 items-center gap-1.5">
      {values.map((v) => (
        <span key={v} className="inline-flex min-w-0 items-center gap-1">
          {isColor && <Swatch value={v} className="h-3 w-3" />}
          {/* attrWord, NOT titleCase: the latter turns a hyphen into a space and renders the age
              band `30-45` as "30 45" — two numbers where there was a range. */}
          <span className="truncate text-[13px] font-semibold text-foreground">{attrWord(v)}</span>
        </span>
      ))}
      {isColor && <span className="shrink-0 text-[11px] text-muted-foreground">{label}</span>}
    </span>
  );
}

/**
 * One subject's crop, with the placeholder as its FALLBACK rather than only its alternative.
 *
 * `renderThumb` returning null already meant "no crop for this subject", and that path drew the
 * index on the card's own surface. What had no path was a crop that is REFERENCED and does not
 * load: the event carries a `personCrop2` key, the file behind it was never written (no
 * appearance pass ran, storage pruned it), and the caller's `<img>` renders as a broken element
 * inside a dark box — reported, exactly right, as "showing a black image".
 *
 * A reference is a claim, and this is where the claim gets checked. On error the element is
 * dropped and the same placeholder takes over, so a missing crop looks identical however it came
 * to be missing.
 */
function SubjectThumb({ seq, thumb }: { seq: number; thumb: ReactNode }) {
  const [failed, setFailed] = useState(false);

  return (
    <div className="h-[72px] w-[72px] shrink-0 overflow-hidden rounded-lg border border-border bg-muted">
      {thumb && !failed ? (
        // The caller owns the <img>; the error is caught here, in the CAPTURE phase — an image's
        // `error` event does not bubble, so a listener on the parent never sees it otherwise.
        <div
          className="h-full w-full"
          onErrorCapture={() => setFailed(true)}
        >
          {thumb}
        </div>
      ) : (
        // No crop, or a crop that would not load. The index still identifies this subject against
        // the snapshot strip, so the placeholder carries it rather than being blank.
        <div className="flex h-full w-full items-center justify-center font-mono text-[13px] font-semibold text-muted-foreground">
          #{seq}
        </div>
      )}
    </div>
  );
}

/**
 * ONE SUBJECT'S ATTRIBUTES AS RECORD FACTS — what the details panel prints beside their crop.
 *
 * The record's own facts describe the EVENT: when, which camera, which detector, how many people
 * were in the frame. Appearance is not one of those. It belongs to a person, and on a frame of
 * ten there are ten different answers — so a panel that prints one set of them beside a picture
 * of whichever crop is selected is, most of the time, describing somebody else.
 *
 * Same vocabulary as the list above (`fieldsFor`, `attrWord`), so a value reads identically
 * wherever it appears. A field the subject has no reading for prints an em dash rather than
 * being dropped: on a frame where somebody's shirt WAS read, "not read for this one" is a
 * finding about this one.
 */
export function subjectAttributeFacts(subject: EventSubject): EventFact[] {
  return fieldsFor(subject).map((field) => {
    const values = valuesOf(subject.attributes?.[field]);
    return {
      key: `subject_${field}`,
      label: FIELD_LABEL[field] || titleCase(field),
      value: values.length ? values.map(attrWord).join(', ') : '—',
    };
  });
}

/**
 * ONE SUBJECT'S WORDS — who they are, and what was read about them.
 *
 * Extracted because the same summary is now printed in two places: under the crop in the
 * snapshot viewer, which is where an operator looks at a person, and in the list below for the
 * subjects that HAVE no crop to be printed under. Two renderings of "what the module saw" would
 * be two chances to describe the same detection differently.
 *
 * `among` is the other subjects in the frame, and it decides which fields are COLUMNS: a field
 * nobody in this frame has is not mentioned, and a field somebody has is mentioned for everybody
 * — blank included, because "not read for this one" is a finding rather than an absence.
 */
export function SubjectSummary({
  subject,
  among,
  marked = false,
  className = '',
}: {
  subject: EventSubject;
  among: EventSubject[];
  /** Mark this as the subject the record was opened from. */
  marked?: boolean;
  className?: string;
}) {
  const plan = useMemo(() => planFor(among.length ? among : [subject]), [among, subject]);

  const secondary = plan.rest
    .filter((f) => !(f === 'plate' && subject.subject_label))
    .map((f) => {
      const vs = valuesOf(subject.attributes?.[f]);
      if (!vs.length) return null;
      return `${FIELD_LABEL[f] || titleCase(f)} ${vs.map(attrWord).join(', ')}`;
    })
    .filter(Boolean)
    .join(' · ');

  return (
    <div className={`flex min-w-0 flex-1 flex-col gap-1 py-0.5 ${className}`}>
      <div className="flex min-w-0 items-center gap-1.5">
        {/* The module's OWN index — the number the crop is named after, so "#2" here and
            "person Crop 2" in the snapshot strip are the same person. */}
        <span className="shrink-0 font-mono text-[11px] font-semibold text-muted-foreground">
          #{subject.subject_seq}
        </span>
        {subject.subject_label && (
          <span className="truncate text-[13px] font-semibold text-foreground">
            {subject.subject_label}
          </span>
        )}
        {marked && (
          <span className="shrink-0 rounded bg-brand px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-white">
            This one
          </span>
        )}
      </div>

      {plan.lead.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {plan.lead.map((f) => (
            <LeadValue key={f} field={f} values={valuesOf(subject.attributes?.[f])} />
          ))}
        </div>
      )}

      {plan.rest.length > 0 && (
        <div className="truncate text-[12px] text-muted-foreground" title={secondary || undefined}>
          {/* An em dash, not an empty line: with the field a column for this frame, nothing here
              means this person was not read — which is a finding. */}
          {secondary || '—'}
        </div>
      )}

      {plan.lead.length === 0 && plan.rest.length === 0 && (
        <span className="text-[12px] text-muted-foreground">
          No appearance recorded for this detection.
        </span>
      )}
    </div>
  );
}

export default function SubjectBreakdown({
  subjects,
  highlightSeq = null,
  renderThumb,
  onSelect,
  className = '',
}: SubjectBreakdownProps): ReactNode {
  const rows = subjects || [];
  // Recomputed only when the list changes: the plan walks every subject's keys, and the record
  // re-renders on every gallery click.
  const plan = useMemo(() => planFor(rows), [rows]);

  if (rows.length === 0) return null;

  return (
    <div className={className}>
      <h3 className="mb-3 flex items-baseline gap-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        <span>In this frame</span>
        {/* The count as a chip rather than as part of the heading: it is the answer to "how
            many", and a reader scanning for a number should not have to read a sentence. */}
        <span className="rounded-md bg-brand-light px-1.5 py-0.5 text-[11px] font-semibold normal-case tracking-normal text-brand">
          {headline(rows)}
        </span>
        {onSelect && rows.length > 1 && (
          <span className="ml-auto text-[10px] font-normal normal-case tracking-normal text-muted-foreground">
            Select one to see it above
          </span>
        )}
      </h3>

      {/* Two up where there is room, one where there is not. A subject is a face plus two short
          lines, and squeezing two of those into a narrow modal makes both unreadable. */}
      <div className="grid gap-2 sm:grid-cols-2">
        {rows.map((s) => {
          const isOpen = highlightSeq != null && s.subject_seq === highlightSeq;
          const selectable = Boolean(onSelect && s.crop_key);

          /**
           * The supporting line: every other field somebody in this frame carries, LABELLED.
           *
           * Labelled because the values alone are not self-describing once they leave the
           * headline — an attribute pass reports `direction: unknown`, and a muted line reading
           * just "Unknown" tells the reader nothing about what was unknown.
           *
           * The plate is skipped when it is already the card's title, which it is whenever one
           * was read: printing a number plate twice in two lines is not emphasis, it is noise.
           */
          const body = (
            <>
              {/* The face, as large as the card affords. This is the identification tool — the
                  attributes describe, the picture recognises. */}
              <SubjectThumb seq={s.subject_seq} thumb={renderThumb?.(s)} />
              <SubjectSummary subject={s} among={rows} marked={isOpen} />
            </>
          );

          const shell = [
            'flex w-full items-start gap-3 rounded-xl border p-2.5 text-left transition-colors',
            isOpen ? 'border-brand bg-brand-light' : 'border-border bg-muted',
            selectable ? 'hover:border-brand hover:bg-brand-light' : '',
          ].join(' ');

          // A button only when clicking it does something. A row that looks pressable and is not
          // is worse than a plain one — the reader learns the list is dead and stops trying.
          return selectable ? (
            <button
              key={s.subject_seq}
              type="button"
              onClick={() => onSelect?.(s)}
              aria-pressed={isOpen}
              aria-label={`Show subject ${s.subject_seq}${s.subject_label ? ` — ${s.subject_label}` : ''} in the snapshot viewer`}
              className={`${shell} focus:outline-none focus-visible:ring-2 focus-visible:ring-brand`}
            >
              {body}
            </button>
          ) : (
            <div key={s.subject_seq} className={shell}>
              {body}
            </div>
          );
        })}
      </div>

      {/* What this camera did NOT report, said once rather than as an empty cell on every card.
          It is the difference between "nobody was carrying anything" and "carrying is not
          collected here", and only one of those is true. */}
      {plan.absent.length > 0 && (
        <p className="mt-2.5 text-[11px] text-muted-foreground">
          Not collected on this camera:{' '}
          {plan.absent.map((f) => FIELD_LABEL[f] || titleCase(f)).join(', ')}.
        </p>
      )}
    </div>
  );
}
